import React, { useEffect, useState } from 'react';
import TopBar from './components/TopBar';
import Sidebar from './components/Sidebar';
import CorpTable from './components/CorpTable';
import CorpFormScreen from './components/CorpFormScreen';
import VendorTable from './components/VendorTable';
import VendorForm from './components/VendorForm';
import EmployeeTable from './components/EmployeeTable';
import EmployeeForm from './components/EmployeeForm';
import InvoiceTable from './components/InvoiceTable';
import InvoiceForm from './components/InvoiceForm';
import BillingTable from './components/BillingTable';
import BillingFormScreen from './components/BillingFormScreen';
import TimesheetTable from './components/TimesheetTable';
import TimesheetForm from './components/TimesheetForm';
import TimesheetItemTable from './components/TimesheetItemTable';
import AdminTable from './components/AdminTable';
import AdminForm from './components/AdminForm';
import LandingScreen from './components/LandingScreen';
import Security from './components/Security';
import { fetchWithCorpId } from './utils/api';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import { useAuth, AuthProvider } from './utils/auth';
import QuickTime from './components/QuickTime';
import PasswordReset from './components/PasswordReset';
import Dashboard from './components/Dashboard';

// Import CSS files
import './styles/base.css';
import './styles/layout.css';
import './styles/buttons.css';
import './styles/forms.css';
import './styles/components.css';

// Protected Route component
const ProtectedRoute = ({ children, selectedCorp }) => {
  const { user, isInitialized } = useAuth();
  const [activeRequests, setActiveRequests] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    const handler = (e) => setActiveRequests(e.detail || 0);
    window.addEventListener('api:active', handler);
    return () => window.removeEventListener('api:active', handler);
  }, []);
  if (!isInitialized) return null;
  if (!user) {
    return <Navigate to="/on/login" replace />;
  }
  return (
    <div className="layout">
      {activeRequests > 0 && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(255,255,255,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div className="spinner" style={{
            width: '48px',
            height: '48px',
            border: '4px solid #cbd5e1',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
        </div>
      )}
      <TopBar selectedCorp={selectedCorp} />
      <div className="content">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
        <main>
          {children}
        </main>
      </div>
    </div>
  );
};

// Main App component
const AppContent = () => {
  const [selectedCorp, setSelectedCorp] = useState(null);
  const navigate = useNavigate();

  // Load selected corporation on mount
  useEffect(() => {
    const loadSelectedCorp = async () => {
      const storedCorpData = localStorage.getItem('selectedCorpData');
      if (storedCorpData) {
        setSelectedCorp(JSON.parse(storedCorpData));
        localStorage.removeItem('selectedCorpData');
        return;
      }
      const corpId = localStorage.getItem('selectedCorpId');
      if (corpId) {
        try {
          const corp = await fetchWithCorpId(`/corps/${corpId}`);
          setSelectedCorp(corp);
        } catch (error) {
          localStorage.removeItem('selectedCorpId');
        }
      }
    };
    loadSelectedCorp();
  }, []);

  const handleCorpSwitch = (corp) => {
    setSelectedCorp(corp);
    localStorage.setItem('selectedCorpId', corp.id);
  };

  // Edit handlers for each entity type
  const handleCorpEdit = (corp) => {
    navigate(`/on/corps/edit/${corp.id}`);
  };
  const handleVendorEdit = (vendor) => {
    navigate(`/on/vendors/edit/${vendor.id}`);
  };
  const handleEmployeeEdit = (employee) => {
    navigate(`/on/employees/edit/${employee.id}`);
  };
  const handleInvoiceEdit = (invoice) => {
    navigate(`/on/invoices/edit/${invoice.id}`);
  };
  const handleBillingEdit = (billing) => {
    navigate(`/on/billings/edit/${billing.id}`);
  };
  const handleTimesheetEdit = (timesheet) => {
    navigate(`/on/timesheets/edit/${timesheet.id}`);
  };
  const handleAdminEdit = (admin) => {
    navigate(`/on/admin/edit/${admin.id}`);
  };

  // Success handlers for each entity type
  const handleCorpEditComplete = () => {
    navigate('/on/corps');
    refreshSelectedCorp();
  };
  const handleVendorEditComplete = () => {
    navigate('/on/vendors');
  };
  const handleEmployeeEditComplete = () => {
    navigate('/on/employees');
  };
  const handleInvoiceEditComplete = () => {
    navigate('/on/invoices');
  };
  const handleBillingEditComplete = () => {
    navigate('/on/billings');
  };
  const handleTimesheetEditComplete = () => {
    navigate('/on/timesheets');
  };
  const handleAdminEditComplete = () => {
    navigate('/on/admin');
  };

  // Helper function to refresh selected corporation
  const refreshSelectedCorp = async () => {
    const corpId = localStorage.getItem('selectedCorpId');
    if (corpId) {
      try {
        const corp = await fetchWithCorpId(`/corps/${corpId}`);
        setSelectedCorp(corp);
      } catch (error) {
        localStorage.removeItem('selectedCorpId');
      }
    }
  };

  const handleTimesheetView = (timesheet) => {
    navigate(`/on/timesheet-items?timesheetId=${timesheet.id}`);
  };

  return (
    <Routes>
      <Route path="/on" element={<Navigate to="/on/landing" replace />} />
      <Route path="/on" >
        <Route path="login" element={<Login />} />
        <Route path="password-reset" element={<PasswordReset />} />
        <Route path="dashboard" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="landing" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <LandingScreen />
          </ProtectedRoute>
        } />
        <Route path="corps" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <CorpTable
              onEdit={handleCorpEdit}
              selectedCorp={selectedCorp}
              onCorpSwitch={handleCorpSwitch}
            />
          </ProtectedRoute>
        } />
        <Route path="corps/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <CorpFormScreen
              mode="create"
              onSuccess={handleCorpEditComplete}
              onCancel={() => navigate('/on/corps')}
            />
          </ProtectedRoute>
        } />
        <Route path="corps/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <CorpFormScreen
              mode="edit"
              onSuccess={handleCorpEditComplete}
              onCancel={() => navigate('/on/corps')}
            />
          </ProtectedRoute>
        } />
        <Route path="vendors" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <VendorTable onEdit={handleVendorEdit} />
          </ProtectedRoute>
        } />
        <Route path="vendors/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <VendorForm
              mode="create"
              onSuccess={handleVendorEditComplete}
              onCancel={() => navigate('/on/vendors')}
            />
          </ProtectedRoute>
        } />
        <Route path="vendors/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <VendorForm
              mode="edit"
              onSuccess={handleVendorEditComplete}
              onCancel={() => navigate('/on/vendors')}
            />
          </ProtectedRoute>
        } />
        <Route path="employees" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <EmployeeTable onEdit={handleEmployeeEdit} />
          </ProtectedRoute>
        } />
        <Route path="employees/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <EmployeeForm
              mode="create"
              onSuccess={handleEmployeeEditComplete}
              onCancel={() => navigate('/on/employees')}
            />
          </ProtectedRoute>
        } />
        <Route path="employees/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <EmployeeForm
              mode="edit"
              onSuccess={handleEmployeeEditComplete}
              onCancel={() => navigate('/on/employees')}
            />
          </ProtectedRoute>
        } />
        <Route path="invoices" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <InvoiceTable onEdit={handleInvoiceEdit} />
          </ProtectedRoute>
        } />
        <Route path="invoices/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <InvoiceForm
              mode="create"
              onSuccess={handleInvoiceEditComplete}
              onCancel={() => navigate('/on/invoices')}
            />
          </ProtectedRoute>
        } />
        <Route path="invoices/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <InvoiceForm
              mode="edit"
              onSuccess={handleInvoiceEditComplete}
              onCancel={() => navigate('/on/invoices')}
            />
          </ProtectedRoute>
        } />
        <Route path="billings" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <BillingTable onEdit={handleBillingEdit} />
          </ProtectedRoute>
        } />
        <Route path="billings/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <BillingFormScreen
              mode="create"
              onSuccess={handleBillingEditComplete}
              onCancel={() => navigate('/on/billings')}
            />
          </ProtectedRoute>
        } />
        <Route path="billings/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <BillingFormScreen
              mode="edit"
              onSuccess={handleBillingEditComplete}
              onCancel={() => navigate('/on/billings')}
            />
          </ProtectedRoute>
        } />
        <Route path="timesheets" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <TimesheetTable onEdit={handleTimesheetEdit} onView={handleTimesheetView} />
          </ProtectedRoute>
        } />
        <Route path="timesheets/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <TimesheetForm
              mode="create"
              onSuccess={handleTimesheetEditComplete}
              onCancel={() => navigate('/on/timesheets')}
            />
          </ProtectedRoute>
        } />
        <Route path="timesheets/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <TimesheetForm
              mode="edit"
              onSuccess={handleTimesheetEditComplete}
              onCancel={() => navigate('/on/timesheets')}
            />
          </ProtectedRoute>
        } />
        <Route path="timesheet-items" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <TimesheetItemTable />
          </ProtectedRoute>
        } />
        <Route path="security" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <Security />
          </ProtectedRoute>
        } />
        <Route path="quick-time" element={<QuickTime />} />
        <Route path="admin" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <AdminTable onEdit={handleAdminEdit} />
          </ProtectedRoute>
        } />
        <Route path="admin/new" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <AdminForm
              mode="create"
              onSuccess={handleAdminEditComplete}
              onCancel={() => navigate('/on/admin')}
            />
          </ProtectedRoute>
        } />
        <Route path="admin/edit/:id" element={
          <ProtectedRoute selectedCorp={selectedCorp}>
            <AdminForm
              mode="edit"
              onSuccess={handleAdminEditComplete}
              onCancel={() => navigate('/on/admin')}
            />
          </ProtectedRoute>
        } />
      </Route>
      <Route path="/" element={<Navigate to="/on/landing" replace />} />
    </Routes>
  );
};

// Wrap the app with AuthProvider only
const App = () => (
  <AuthProvider>
    <Router>
      <AppContent />
    </Router>
  </AuthProvider>
);

export default App;
